<template>
  <div>
    <main-header>
      <v-spacer />
    </main-header>

    <v-container fluid>
      <v-toolbar
        class="toolbar"
        height="84"
        flat
      >
        <div class="toolbar-content-special d-flex align-center w-100">
          <SelectPartners
            v-if="payoutPartnerName && partnersList && partnersList.length"
            :partners-list="partnersList"
            :partner-name="payoutPartnerName"
            @on-change-partner="onChangedSelectedPartner"
          />
          <v-spacer />
          <FilterInput
            v-model="filter"
            :items="auditLogPayoutTableFilters"
            filter-range-from-payload-string=">"
            filter-range-to-payload-string="<"
          />
        </div>
      </v-toolbar>
      <v-divider />

      <v-data-table
        v-if="isRequiredFilterSelected"
        :headers="auditLogPayoutTableHeaders"
        :items="itemsList"
        class="elevation-1 table"
        multi-sort
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        :loading="loadingTable"
        hide-default-footer
        item-key="key"
        :items-per-page="perPage"
        fixed-header
        @update:sort-by="getItemsListWithSort"
        @update:sort-desc="getItemsListWithSort"
      >
        <template v-slot:no-data>
          <v-btn
            color="primary"
            dark
            @click="getItemsList"
          >
            Refresh
          </v-btn>
        </template>

        <template v-slot:footer>
          <v-container
            fluid
            class="mt-4"
          >
            <v-row>
              <v-col
                cols="9"
                align-self="center"
              >
                <v-pagination
                  v-model="page"
                  class="b-pagination"
                  :length="totalPages"
                  :total-visible="11"
                  @input="onChangePage"
                />
              </v-col>
              <v-col cols="3">
                <v-select
                  v-model="perPage"
                  class="select-per-page"
                  :items="perPageItems"
                  placeholder="Items per page"
                  dance
                  @change="perPage = parseInt($event, 10)"
                />
              </v-col>
            </v-row>
          </v-container>
          <div class="text-center py-2" />
        </template>
      </v-data-table>

      <v-alert
        v-else
        density="compact"
        max-width="800"
        style="margin: 2rem auto"
        variant="plain"
        title="Alert title"
        type="warning"
      >
        {{ isRequiredFilterNotSelected }}
      </v-alert>
    </v-container>
  </div>
</template>

<script>
// constants
import {
  auditLogPayoutTableHeaders,
  auditLogPayoutTableFilters
} from "@/utils/staticTablesColumns";
// modules
import { mapGetters } from "vuex";
// components
import MainHeader from "@/components/MainHeader";
import SelectPartners from "@/components/SelectPartners";
import FilterInput from "@/components/FilterInput";

export default {
  name: "AuditLogPayout",

  components: {
    MainHeader,
    SelectPartners,
    FilterInput
  },

  data() {
    return {
      auditLogPayoutTableHeaders,
      auditLogPayoutTableFilters,
      payoutPartnerName: null,
      baseCurrency: null,

      restoreFromQuery: false,
      sortBy: [],
      sortDesc: [],
      loadingTable: false,
      perPage: 10,
      page: 1,
      filter: [],
      perPageItems: [10, 25, 50, 100, 500]
    }
  },

  computed: {
    ...mapGetters({
      itemsList: "AuditLogStore/auditLogPayout",
      pagination: "AuditLogStore/auditLogPayoutPagination",
      partnersList: "ProviderRatesStore/partnersList"
    }),

    totalPages() {
      return this.pagination.totalPages || 0;
    },

    filterStringPayload() {
      const filterObject = {};

      this.filter.forEach(item => {
        return filterObject[item.key] = item.value;
      });
      return filterObject
    },

    filterStringURL: {
      get() {
        return this.filter.map(item => {
          return `${item.key}:${item.value}:${item.name}`;
        }).join(",");
      },
      set(newValue) {
        const filterArray = newValue.split(",");

        filterArray.forEach((item, index) => {
          const filterKeyValue = item.split(":")
          this.filter[index] = {};
          this.filter[index].key = filterKeyValue[0];
          this.filter[index].value = filterKeyValue[1];
          this.filter[index].name = filterKeyValue[2] || "";
        });

        return newValue;
      }
    },

    isRequiredFilterSelected () {
      if (!!this.filterStringPayload.receiveCurrency) {
        return true;
      } else {
        return false;
      }
    },

    isRequiredFilterNotSelected () {
      return this.isRequiredFilterSelected ? '' : 'Please select filter: Receive Currency';
    }
  },


  watch: {
    perPage (newValue) {
      if (this.restoreFromQuery) return;

      this.addQueryToRouteWithReload("perPage", newValue);
      this.addQueryToRouteWithReload("page", 1);
    },
    filter: {
      handler() {
        if (this.restoreFromQuery) return;

        this.getItemsListWithFilters();
      },
      deep: true
    }
  },

  beforeCreate() {
    const query = this.$route.query;
    const hasQueryPayoutPartnerName = query.payoutPartnerName;

    if (hasQueryPayoutPartnerName) {
      this.payoutPartnerName = query.payoutPartnerName;
    }

    this.$store.dispatch("ProviderRatesStore/getPartners")
      .then((data) => {
        if (!hasQueryPayoutPartnerName && !!data) {
          this.payoutPartnerName = data[0].name;
          this.baseCurrency = data[0].baseCurrency
        }

        this.getItemsList();
      });
  },

  created() {
    if (this.$route.query) {
      this.restoreFromQuery = true;
      const query = this.$route.query;
      this.perPage = parseInt(query.perPage) || 10;
      this.page = parseInt(query.page) || 1;
      let sort = query.sort || [];

      if (sort.length) {
        if (typeof sort === "string") sort = [sort];

        const sortBy = [];
        const sortDesc = [];
        sort.forEach(item => {
          const array = item.split(",");
          sortBy.push(array[0]);
          sortDesc.push(array[1] !== "asc");
        });
        this.sortBy = sortBy;
        this.sortDesc = sortDesc;
      }

      if (query?.filter?.length) {
        this.filterStringURL = query.filter;
      }

      if (query.payoutPartnerName) {
        this.payoutPartnerName = query.payoutPartnerName;
      }

      if (query.sendingCurrency) {
        this.baseCurrency = query.sendingCurrency;
      }
    }

    this.getItemsList();
  },

  methods: {
    onChangedSelectedPartner (value) {
      const partnerName = value.name;
      const baseCurrency = value.baseCurrency;

      this.addQueryToRouteWithReload("payoutPartnerName", partnerName);
      this.addQueryToRouteWithReload("page", 1);
      this.payoutPartnerName = partnerName;
      this.baseCurrency = baseCurrency;
    },

    async getItemsList () {
      try {
        this.loadingTable = true;

        const payload = {
          page: this.page,
          size: this.perPage,
          sort: this.sortBy.map(
            (val, index) => `${val},${this.sortDesc[index] ? "desc" : "asc"}`
          ),
          payoutPartnerName: this.payoutPartnerName,
          sendingCurrency: this.baseCurrency,
          ...this.filterStringPayload,
        };

        if (this.isRequiredFilterSelected) {
          await this.$store.dispatch("AuditLogStore/getAuditLogPayout", payload)
        } else {
          return;
        }

      } finally {
        this.loadingTable = false;
        this.restoreFromQuery = false;
      }
    },

    getItemsListWithSort () {
      if (this.loadingTable) return;
      this.page = 1;
      let sort = this.sortBy.map((val, index) => {
        return `${val},${this.sortDesc[index] ? "desc" : "asc"}`;
      });
      this.addQueryToRouteWithReload("page", 1);
      this.addQueryToRouteWithReload("sort", sort);
    },

    addQueryToRoute(name, value) {
      this.$router.replace(
        { query: Object.assign({ ...this.$route.query }, { [name]: value }) },
        () => {}
      );
    },

    addQueryToRouteWithReload (name, value) {
      this.$router.replace({
        query: Object.assign({
          ...this.$route.query
        }, {
          [name]: value
        })},
      () => {}
      );
    },

    getItemsListWithFilters () {
      this.addQueryToRouteWithReload("filter", this.filterStringURL);
      this.addQueryToRouteWithReload("perPage", this.perPage);
      this.addQueryToRouteWithReload("page", 1);
    },

    onChangePage () {
      this.addQueryToRouteWithReload("page", this.page);
    }
  }
}
</script>
